import httpClient from '../lib/httpClient';
import type {
  TabNudge,
  TabNudgeCreateRequest,
  TabNudgeUpdateRequest,
  TabNudgeListParams,
  TabNudgeListResponse,
  TabNudgeTargetsResponse,
} from '../types/TabNudge';

const BASE_URL = '/api/v1/reels-cms/tab-nudge';

/**
 * List tab-nudge entries for the admin table, optionally filtered by tenant
 * or tab, with pagination.
 */
export const listTabNudges = async (
  params: TabNudgeListParams = {}
): Promise<TabNudgeListResponse> => {
  try {
    const query = new URLSearchParams();
    if (params.tenant) query.append('tenant', params.tenant);
    if (params.targetId) query.append('targetId', params.targetId);
    if (params.page !== undefined) query.append('page', params.page.toString());
    if (params.size !== undefined) query.append('size', params.size.toString());

    const queryString = query.toString();
    return await httpClient.get(queryString ? `${BASE_URL}?${queryString}` : BASE_URL);
  } catch (error) {
    console.error('Unable to fetch tab-nudge entries');
    throw error;
  }
};

/**
 * List the nudgeable tabs discovered from the live layouts.
 */
export const listTabNudgeTargets = async (): Promise<TabNudgeTargetsResponse> => {
  try {
    return await httpClient.get(`${BASE_URL}/targets`);
  } catch (error) {
    console.error('Unable to fetch tab-nudge targets');
    throw error;
  }
};

/**
 * Fetch a single tab-nudge entry by id.
 */
export const getTabNudge = async (id: string): Promise<TabNudge> => {
  try {
    return await httpClient.get(`${BASE_URL}/${id}`);
  } catch (error) {
    console.error(`Unable to fetch tab-nudge entry ${id}`);
    throw error;
  }
};

/**
 * Create a new tab-nudge entry.
 */
export const createTabNudge = async (request: TabNudgeCreateRequest): Promise<TabNudge> => {
  try {
    return await httpClient.post(BASE_URL, request);
  } catch (error) {
    console.error('Unable to create tab-nudge entry');
    throw error;
  }
};

/**
 * Partially update a tab-nudge entry — toggle enable/disable or edit the
 * window. Omitted fields are left unchanged.
 */
export const updateTabNudge = async (
  id: string,
  request: TabNudgeUpdateRequest
): Promise<TabNudge> => {
  try {
    return await httpClient.put(`${BASE_URL}/${id}`, request);
  } catch (error) {
    console.error(`Unable to update tab-nudge entry ${id}`);
    throw error;
  }
};

/**
 * Hard-delete a tab-nudge entry. Disabling via updateTabNudge is the normal
 * path; delete is for permanent removal.
 */
export const deleteTabNudge = async (id: string): Promise<void> => {
  try {
    await httpClient.delete(`${BASE_URL}/${id}`, {});
  } catch (error) {
    console.error(`Unable to delete tab-nudge entry ${id}`);
    throw error;
  }
};
